import React from 'react';
import { FiGlobe, FiTrendingUp,FiClock, FiShield, FiZap, FiBarChart2,  FiAward } from 'react-icons/fi';

const CloudVsOnPremise = () => {
  const cloudBenefits = [
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'No Infrastructure Investment',
      description: 'Cloud ERPs require no servers, no IT infrastructure, and no hardware maintenance. You pay only for what you use. This is especially valuable for institutions with limited IT budgets or technical staff.',
      stat: 'Cloud ERP reduces infrastructure costs by 60-80%',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Instant Access from Anywhere',
      description: 'Teachers, administrators, and parents can access the system from any device, anywhere, anytime. No VPNs, no remote access setup, no location restrictions. The institution is always connected.',
      stat: '85% of institutions prefer cloud for remote accessibility',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Automatic Updates and Maintenance',
      description: 'Updates happen automatically—no manual installations, no downtime scheduling, no IT intervention. New features are available immediately. Security patches are applied instantly.',
      stat: 'Cloud systems have 99.9% uptime with automated maintenance',
      color: 'bg-amber-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Scalable for Growth',
      description: 'Add users, institutions, or features as you grow. No capacity planning, no hardware upgrades, no implementation delays. The cloud scales with your institution.',
      stat: '90% of institutions cite scalability as a key cloud benefit',
      color: 'bg-rose-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Built for Multi-Campus Management',
      description: 'Cloud ERPs are designed for multiple locations. Data flows seamlessly across campuses. Central visibility, local execution. Perfect for educational trusts and groups.',
      stat: '70% of multi-campus institutions use cloud ERPs',
      color: 'bg-purple-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Lower Total Cost of Ownership',
      description: 'No upfront capital expenditure. No hidden maintenance costs. No unexpected IT upgrades. Cloud ERPs offer predictable, affordable subscription pricing.',
      stat: 'Cloud ERPs are 40-60% cheaper over 5 years than on-premise',
      color: 'bg-blue-50'
    }
  ];

  const onPremiseBenefits = [
    {
      title: 'Data Control',
      description: 'All data stays on your own servers. Complete control over where data is stored and who can access it.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Internet Independence',
      description: 'No reliance on internet connectivity. The system works even when the internet is down.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Customization Freedom',
      description: 'Full access to the codebase. Complete customization without vendor limitations.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Legacy Integration',
      description: 'Easier integration with existing legacy systems that are on-premise.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Data Sovereignty',
      description: 'Data remains within the institution\'s geographic location. Important for regulatory compliance.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Long-term Control',
      description: 'No dependency on vendor for uptime or performance. You control your own destiny.',
      icon: <FiTrendingUp className="w-5 h-5" />
    }
  ];

  const comparisonTable = [
    {
      feature: 'Initial Investment',
      cloud: 'Low (pay-as-you-go)',
      onPremise: 'High (servers, licenses, infrastructure)'
    },
    {
      feature: 'Maintenance',
      cloud: 'Included (vendor managed)',
      onPremise: 'In-house (IT team required)'
    },
    {
      feature: 'Updates',
      cloud: 'Automatic and continuous',
      onPremise: 'Manual, scheduled, and expensive'
    },
    {
      feature: 'Scalability',
      cloud: 'Instant and effortless',
      onPremise: 'Complex and expensive hardware upgrades'
    },
    {
      feature: 'Accessibility',
      cloud: 'Anywhere, any device',
      onPremise: 'Limited to campus network'
    },
    {
      feature: 'IT Staff Required',
      cloud: 'Minimal',
      onPremise: 'Dedicated team needed'
    },
    {
      feature: 'Disaster Recovery',
      cloud: 'Vendor managed, automated',
      onPremise: 'In-house, complex setup'
    },
    {
      feature: 'Total Cost of Ownership',
      cloud: 'Predictable and affordable',
      onPremise: 'Unpredictable and high'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Choosing between cloud-based and on-premise ERP is one of the most important decisions an educational institution can make. It affects cost, accessibility, security, scalability, and the future of your digital transformation journey.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">The cloud vs. on-premise debate is not just about technology—it's about strategy.</strong> It's about how you want to operate, how you want to grow, and where you want to invest your resources. For Indian institutions, the choice has significant implications.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's a complete guide to help you understand the differences and make the right decision for your institution.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">60-80%</div>
          <div className="text-sm text-slate-500">Cost savings with cloud ERP</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">99.9%</div>
          <div className="text-sm text-slate-500">Cloud uptime with automated maintenance</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">85%</div>
          <div className="text-sm text-slate-500">Prefer cloud for accessibility</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Cloud ERP is Transforming Indian Education</h2>

      {/* Cloud Benefits */}
      {cloudBenefits.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">💡 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* On-Premise Considerations */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">When On-Premise Makes Sense</h3>
        <p className="text-slate-600 text-sm mb-4">Some institutions still choose on-premise ERP. Here are the reasons:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {onPremiseBenefits.map((item, i) => (
            <div key={i} className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Table */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 overflow-x-auto">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Cloud vs. On-Premise: A Detailed Comparison</h3>
        <div className="w-full">
          <div className="grid grid-cols-3 gap-2 text-xs sm:text-sm">
            <div className="font-bold text-slate-900 p-2 border-b border-slate-200">Feature</div>
            <div className="font-bold text-indigo-600 p-2 border-b border-slate-200 text-center">Cloud ERP</div>
            <div className="font-bold text-slate-600 p-2 border-b border-slate-200 text-center">On-Premise</div>
            
            {comparisonTable.map((item, i) => (
              <React.Fragment key={i}>
                <div className="p-2 border-b border-slate-100">{item.feature}</div>
                <div className="p-2 border-b border-slate-100 text-center text-indigo-600 font-medium">{item.cloud}</div>
                <div className="p-2 border-b border-slate-100 text-center text-slate-600">{item.onPremise}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Indian Context */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Indian Context</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          For Indian educational institutions, cloud ERP offers unique advantages. With the rapid expansion of internet connectivity, mobile penetration, and digital infrastructure, cloud adoption is accelerating across the country. Institutions in tier-2 and tier-3 cities can now access world-class ERP systems without investing in expensive infrastructure.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          Government initiatives like Digital India and the National Education Policy 2020 are driving digital transformation. Cloud ERPs align perfectly with these national priorities. They enable institutions to be more efficient, transparent, and accessible.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>For most Indian institutions, cloud is the future.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Cloud ERP for India</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">☁️</div>
            <h4 className="font-bold text-slate-900 text-sm">100% Cloud-Based</h4>
            <p className="text-xs text-slate-600">No infrastructure costs, accessible anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🇮🇳</div>
            <h4 className="font-bold text-slate-900 text-sm">Built for India</h4>
            <p className="text-xs text-slate-600">Designed for Indian educational institutions</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💰</div>
            <h4 className="font-bold text-slate-900 text-sm">Zero Upfront Cost</h4>
            <p className="text-xs text-slate-600">Free to start, pay only as you grow</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Enterprise Security</h4>
            <p className="text-xs text-slate-600">Data encrypted and protected</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-First</h4>
            <p className="text-xs text-slate-600">Access from any device, anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Auto Updates</h4>
            <p className="text-xs text-slate-600">Always up-to-date with latest features</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Choose Cloud with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          The debate between cloud and on-premise ERP is becoming clearer. Cloud offers lower costs, better accessibility, automatic updates, and effortless scalability. For Indian institutions, cloud is the future.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is a 100% cloud-based ERP built for Indian education. No infrastructure costs. Accessible anywhere. Automatic updates. Enterprise security. Zero upfront cost. Free to start.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for cloud ERP.</strong> The future of educational management is in the cloud.
        </p>
      </div>
    </>
  );
};

export default CloudVsOnPremise;